import { useAuthContext } from "../../auth/state/AuthContext";
import { log, logE, logV } from "../../shared/services/debug-logger";
import type {
  AdminAgentConversation,
  AdminAgentConversationSummary,
  AdminAgentPendingAction,
  AdminAgentSettings,
} from "../models/admin-agent.models";
import {
  createAdminAgentConversation,
  deleteAdminAgentConversation,
  getAdminAgentConversation,
  listAdminAgentConversations,
  renameAdminAgentConversation,
  resumeAdminAgentConversation,
  sendAdminAgentMessage,
  stopAdminAgentConversation,
  updateAdminAgentSettings,
  decideAdminAgentApproval,
} from "../services/admin-agent.service";
import {
  adminAgentDraftsAtom,
  adminAgentResumeAttemptsAtom,
  adminAgentSelectedConversationAtom,
  adminAgentSendAttemptsAtom,
} from "../state/admin-agent.atoms";
import { ADMIN_AGENT_POLL_INTERVAL_MS } from "../utils/admin-agent.constants";
import { createAdminAgentRequestId, getAdminAgentErrorMessage } from "../utils/admin-agent.utils";
import axios from "axios";
import { useAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";

// Observe the backend's caller-filtered conversations only while the panel is mounted.
// Polling runs serially, and old reads cannot overwrite a mutation or a newly selected chat.
export const useAdminAgentConversations = () => {
  const accessToken = useAuthContext().state.user?.accessTokenRaw;
  const [selectedId, setSelectedId] = useAtom(adminAgentSelectedConversationAtom);
  const [drafts, setDrafts] = useAtom(adminAgentDraftsAtom);
  const [sendAttempts, setSendAttempts] = useAtom(adminAgentSendAttemptsAtom);
  const [resumeAttempts, setResumeAttempts] = useAtom(adminAgentResumeAttemptsAtom);
  const [conversations, setConversations] = useState<AdminAgentConversationSummary[]>([]);
  const [conversation, setConversation] = useState<AdminAgentConversation>();
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState("");
  const [pollError, setPollError] = useState("");
  const [pendingAction, setPendingAction] = useState<AdminAgentPendingAction>();
  const mounted = useRef(false);
  const activeId = useRef(selectedId);
  const mutationVersion = useRef(0);
  const pending = useRef(false);
  const reading = useRef(false);
  activeId.current = selectedId;

  const conversationId = conversation?.id;
  const conversationStatus = conversation?.status;
  const awaitingApproval = Boolean(conversation?.pendingApproval);
  useEffect(() => {
    if (conversationId)
      log("STATE - useAdminAgentConversations - status", { status: conversationStatus, awaitingApproval });
  }, [conversationId, conversationStatus, awaitingApproval]);
  const pollingFailed = Boolean(pollError);
  useEffect(() => {
    if (pollingFailed) logE(new Error("STATE - refreshConversations - unavailable"));
  }, [pollingFailed]);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const selectConversation = useCallback(
    (id: string | undefined) => {
      log("STATE - selectConversation", { conversationId: id });
      activeId.current = id;
      setSelectedId(id);
      setConversation(undefined);
      setLoading(true);
      setActionError("");
      setPollError("");
    },
    [setSelectedId],
  );

  const acceptConversation = useCallback(
    (snapshot: AdminAgentConversation) => {
      // A different run proves that a timed-out resume was accepted by the backend.
      // Retire that request ID so a later intentional resume starts a new run on its first click.
      setResumeAttempts((current) => {
        const attempt = current[snapshot.id];

        if (!attempt || !snapshot.run || snapshot.run.id === attempt.previousRunId) {
          return current;
        }

        const next = { ...current };
        delete next[snapshot.id];

        return next;
      });
      setConversations((current) =>
        [snapshot, ...current.filter(({ id }) => id !== snapshot.id)].sort((left, right) =>
          right.updatedAt.localeCompare(left.updatedAt),
        ),
      );

      if (activeId.current === snapshot.id) {
        setConversation((current) =>
          current?.id === snapshot.id && current.revision >= snapshot.revision ? current : snapshot,
        );
        setLoading(false);
      }
    },
    [setResumeAttempts],
  );

  const refreshConversations = useCallback(
    async (signal?: AbortSignal) => {
      if (reading.current || pending.current) {
        return;
      }

      reading.current = true;
      const version = mutationVersion.current;
      const requestedId = activeId.current;

      try {
        const summaries = await listAdminAgentConversations(accessToken, signal);

        if (!mounted.current || signal?.aborted || version !== mutationVersion.current) {
          return;
        }

        setConversations(summaries);

        if (!requestedId) {
          if (summaries[0] && !activeId.current) {
            selectConversation(summaries[0].id);
          } else {
            setLoading(false);
          }

          setPollError("");

          return;
        }

        const snapshot = await getAdminAgentConversation(accessToken, requestedId, signal);

        if (
          !mounted.current ||
          signal?.aborted ||
          version !== mutationVersion.current ||
          activeId.current !== requestedId
        ) {
          return;
        }

        acceptConversation(snapshot);
        setPollError("");
      } catch (failure) {
        if (
          !mounted.current ||
          signal?.aborted ||
          version !== mutationVersion.current ||
          activeId.current !== requestedId
        ) {
          return;
        }

        if (axios.isAxiosError(failure) && failure.response?.status === 404) {
          selectConversation(undefined);
          setActionError(
            "This chat is unavailable for your current access, was deleted, or the backend restarted. Select another chat or create one.",
          );
        } else if (!axios.isCancel(failure)) {
          // A lost or changed session must not leave a previously authorized transcript visible in this panel.
          setConversation(undefined);
          setConversations([]);
          setPollError(getAdminAgentErrorMessage(failure));
        }

        setLoading(false);
      } finally {
        reading.current = false;
      }
    },
    [acceptConversation, selectConversation, accessToken],
  );

  useEffect(() => {
    const controller = new AbortController();
    logV("STATE - useAdminAgentConversations - SUB conversations");
    let timer: ReturnType<typeof setTimeout>;

    const pollSharedChats = async () => {
      await refreshConversations(controller.signal);

      if (!controller.signal.aborted) {
        timer = setTimeout(pollSharedChats, ADMIN_AGENT_POLL_INTERVAL_MS);
      }
    };

    void pollSharedChats();

    return () => {
      logV("STATE - useAdminAgentConversations - DISPOSE");
      controller.abort();
      clearTimeout(timer);
    };
  }, [selectedId, refreshConversations]);

  const runConversationAction = async (
    action: AdminAgentPendingAction,
    operation: () => Promise<void>,
  ): Promise<boolean> => {
    if (pending.current) {
      log("STATE - runConversationAction - pending", { action });
      return false;
    }

    log("STATE - runConversationAction", { action });
    pending.current = true;
    mutationVersion.current += 1;
    setPendingAction(action);
    setActionError("");

    try {
      await operation();
      log("STATE - runConversationAction - completed", { action });

      return true;
    } catch (failure) {
      const status = axios.isAxiosError(failure) ? failure.response?.status : undefined;
      logE(new Error(`STATE - runConversationAction - ${action} failed`), { status });
      if (mounted.current) {
        setActionError(getAdminAgentErrorMessage(failure));
      }

      return false;
    } finally {
      pending.current = false;
      mutationVersion.current += 1;

      if (mounted.current) {
        setPendingAction(undefined);
      }
    }
  };

  // === CHAT MANAGEMENT ===

  const createConversation = () =>
    runConversationAction("create", async () => {
      const snapshot = await createAdminAgentConversation(accessToken);

      if (mounted.current) {
        selectConversation(snapshot.id);
        acceptConversation(snapshot);
      }
    });

  const renameConversation = (title: string) =>
    runConversationAction("rename", async () => {
      if (!selectedId) {
        return;
      }

      const snapshot = await renameAdminAgentConversation(accessToken, selectedId, title.trim());

      if (mounted.current) {
        acceptConversation(snapshot);
      }
    });

  const changeSettings = (settings: AdminAgentSettings) =>
    runConversationAction("settings", async () => {
      if (!selectedId) {
        return;
      }

      const snapshot = await updateAdminAgentSettings(accessToken, selectedId, settings);

      if (mounted.current) {
        acceptConversation(snapshot);
      }
    });

  const removeConversation = () =>
    runConversationAction("delete", async () => {
      if (!selectedId) {
        return;
      }

      await deleteAdminAgentConversation(accessToken, selectedId);

      if (mounted.current) {
        setConversations((current) => current.filter(({ id }) => id !== selectedId));
        setDrafts((current) => {
          const next = { ...current };
          delete next[selectedId];
          return next;
        });
        setSendAttempts((current) => {
          const next = { ...current };
          delete next[selectedId];
          return next;
        });
        setResumeAttempts((current) => {
          const next = { ...current };
          delete next[selectedId];
          return next;
        });
        selectConversation(undefined);
      }
    });

  // === RUN CONTROL ===

  const draft = selectedId ? drafts[selectedId] ?? "" : "";
  const updateDraft = (message: string) => {
    if (selectedId) {
      setDrafts((current) => ({ ...current, [selectedId]: message }));
    }
  };

  const sendMessage = () =>
    runConversationAction("send", async () => {
      if (!selectedId || !draft.trim()) {
        return;
      }

      const message = draft.trim();
      const previousAttempt = sendAttempts[selectedId];
      const requestId = previousAttempt?.message === message ? previousAttempt.requestId : createAdminAgentRequestId();
      setSendAttempts((current) => ({ ...current, [selectedId]: { message, requestId } }));
      const snapshot = await sendAdminAgentMessage(accessToken, selectedId, message, requestId);

      if (mounted.current) {
        acceptConversation(snapshot);
        setDrafts((current) => ({
          ...current,
          [selectedId]: current[selectedId] === draft ? "" : current[selectedId],
        }));
        setResumeAttempts((current) => {
          const next = { ...current };
          delete next[selectedId];

          return next;
        });
        setSendAttempts((current) => {
          const next = { ...current };
          delete next[selectedId];
          return next;
        });
      }
    });

  const stopConversation = () =>
    runConversationAction("stop", async () => {
      if (!selectedId) {
        return;
      }

      const snapshot = await stopAdminAgentConversation(accessToken, selectedId);

      if (mounted.current) {
        acceptConversation(snapshot);
      }
    });

  const resumeConversation = () =>
    runConversationAction("resume", async () => {
      if (!selectedId) {
        return;
      }

      const previousAttempt = resumeAttempts[selectedId];
      const requestId = previousAttempt?.requestId ?? createAdminAgentRequestId();
      setResumeAttempts((current) => ({
        ...current,
        [selectedId]: previousAttempt ?? { requestId, previousRunId: conversation?.run?.id },
      }));
      const snapshot = await resumeAdminAgentConversation(accessToken, selectedId, requestId);

      if (mounted.current) {
        acceptConversation(snapshot);
        setResumeAttempts((current) => {
          const next = { ...current };
          delete next[selectedId];
          return next;
        });
      }
    });

  const decideApproval = (decision: "approve" | "decline") =>
    runConversationAction("approval", async () => {
      log("STATE - decideApproval", { decision });
      const approvalId = conversation?.pendingApproval?.id;
      if (!selectedId || !approvalId) return;
      const snapshot = await decideAdminAgentApproval(accessToken, selectedId, approvalId, decision);
      if (mounted.current) acceptConversation(snapshot);
    });

  const refreshChats = () => {
    log("STATE - refreshChats");
    setActionError("");

    return refreshConversations();
  };

  return {
    conversations,
    conversation,
    selectedId,
    loading,
    error: actionError || pollError,
    pendingAction,
    draft,
    selectConversation,
    createConversation,
    renameConversation,
    changeSettings,
    removeConversation,
    updateDraft,
    sendMessage,
    stopConversation,
    resumeConversation,
    decideApproval,
    refreshConversations: refreshChats,
  };
};
